import { ZodIssue, ZodSchema } from 'zod';
import {
  DegreeFieldsConfig,
  DegreeFieldsConfigSchema,
  ExactOfferingResponse,
  ExactOfferingResponseSchema,
  Filters,
  FiltersSchema,
  OfferingPayload,
  OfferingResponse,
  OfferingResponseSchema,
  OfferingsSearchPayload,
  OfferingsSearchResponse,
  OfferingsSearchResponseSchema,
  RecommendationsResponse,
  RecommendationsResponseSchema,
  DimensionsResponse,
  DimensionsResponseSchema,
} from './offeringsServiceSchema';

export interface APIRequestOptions extends RequestInit {
  cacheEnabled?: boolean;
  bustCache?: boolean;
  /**
   * Next.js fetch cache hint. Typed loosely (no framework dependency) and
   * forwarded as-is through `fetch`, so RSC callers can set
   * `{ next: { revalidate } }` and reuse the framework data cache.
   */
  next?: { revalidate?: number | false; tags?: string[] };
}

export interface APIClientConfig {
  baseUrl: string;
  defaultHeaders?: Record<string, string>;
  cache?: Map<string, Response>;
  newRelic?: {
    noticeError?: (error: Error, context: Record<string, any>) => void;
  };
  strictValidation?: boolean;
}

export class OfferingsServiceApiClient {
  /**
   * The cache for the API client.
   */
  public cache: Map<string, Response> = new Map();

  /**
   * Constructor for the OfferingsServiceApiClient.
   * @param config - The configuration for the API client.
   */
  constructor(public readonly config: APIClientConfig) {
    const defaultConfig: APIClientConfig = {
      baseUrl: '',
      defaultHeaders: {},
      cache: new Map(),
      newRelic: {
        noticeError: (error: Error, context: Record<string, any>) => {
          console.error(
            'Error in OfferingsServiceApiClient: noticeError',
            error,
            context,
          );
        },
      },
      strictValidation: false,
    };
    this.config = { ...defaultConfig, ...config };
    this.cache = this.config.cache ?? new Map();
  }

  /**
   * Returns the name of the API.
   */
  public getApiName(): string {
    return 'offerings-service';
  }

  /**
   * Makes a GET request to the API.
   */
  public async get(
    path: string,
    options: APIRequestOptions = { cacheEnabled: false },
  ): Promise<Response> {
    const cacheKey = `${this.config.baseUrl}${path}`;

    // Bust cache if bustCache is provided
    if (options.bustCache) {
      this.bustCache(cacheKey);
    }

    if (this.shouldUseCache(cacheKey, options)) {
      return this.cache.get(cacheKey)!.clone();
    }

    const response = await fetch(`${this.config.baseUrl}${path}`, {
      ...options,
      headers: {
        ...this.config.defaultHeaders,
        ...options.headers,
      },
    });
    if (options.cacheEnabled && response.ok) {
      this.cache.set(cacheKey, response.clone());
    }
    return response;
  }

  public async post<Body>(
    path: string,
    body: Body,
    options: APIRequestOptions = { cacheEnabled: false },
  ): Promise<Response> {
    const bodyString = JSON.stringify(body);
    const cacheKey = `${this.config.baseUrl}${path}:${bodyString}`;

    // Bust cache if bustCache is provided
    if (options.bustCache) {
      this.bustCache(cacheKey);
    }

    if (this.shouldUseCache(cacheKey, options)) {
      return this.cache.get(cacheKey)!.clone();
    }

    const response = await fetch(`${this.config.baseUrl}${path}`, {
      method: 'POST',
      body: bodyString,
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...this.config.defaultHeaders,
        ...options.headers,
      },
    });

    if (options.cacheEnabled && response.ok) {
      this.cache.set(cacheKey, response.clone());
    }
    return response;
  }

  private bustCache(cacheKey: string): void {
    this.cache.delete(cacheKey);
  }

  private shouldUseCache(
    cacheKey: string,
    options: APIRequestOptions,
  ): boolean {
    if (!options.cacheEnabled) {
      return false;
    }

    return this.cache.has(cacheKey);
  }

  /**
   * Checks if the response has an error.
   */
  public hasError(response: Response): boolean {
    return response.status >= 400;
  }

  /**
   * Converts the response to a JSON object that is defined by the passed in schema.
   */
  public async toJson<T>(response: Response, schema: ZodSchema<T>): Promise<T> {
    const data = await response.json();

    // Validate the full response as-is
    const validated = schema.safeParse(data);

    if (!validated.success) {
      const error = new Error(`Invalid response for ${this.getApiName()}`);
      console.error(error);
      console.log(this.buildErrorMessage(validated.error.errors));

      if (this.config.strictValidation) {
        throw error;
      }

      return data as T;
    }

    return validated.data;
  }

  /**
   * Reports an error to New Relic.
   */
  public reportError(error: Error, context?: Record<string, any>): void {
    if (this.config.newRelic?.noticeError) {
      this.config.newRelic.noticeError(error, context || {});
    }
  }

  /**
   * Builds an error message from the passed in errors.
   */
  private buildErrorMessage(errors: ZodIssue[]): string[] {
    return errors.map(
      (error) => `Path: ${error.path.join('.')}, Message: ${error.message}`,
    );
  }

  // ─── Offerings Service Methods ───────────────────────────────────────

  /**
   * Get qualified offerings based on the provided payload.
   * @param body - The offering payload.
   * @param userIpAddress - Optional user IP address for geo-location fallbacks.
   * @param options - Optional request options.
   * @returns The offering response.
   */
  async getQualifiedOfferings(
    body: OfferingPayload,
    userIpAddress?: string | null,
    options: APIRequestOptions = {},
  ): Promise<OfferingResponse> {
    const headers: Record<string, string> = {};
    if (userIpAddress) {
      headers['x-forwarded-for'] = userIpAddress;
    }

    const response = await this.post(`/v1/offerings/qualified`, body, {
      ...options,
      headers: { ...options.headers, ...headers },
    });

    if (this.hasError(response)) {
      console.error(`Failed to get qualified offerings`);
    }

    const data = await this.toJson(response, OfferingResponseSchema);
    return data;
  }

  /**
   * Get filters for offerings.
   * @param params - Optional filter parameters (type, subject).
   * @param options - Optional request options.
   * @returns The filters or null if request fails.
   */
  async getFilters(
    params: { type?: string; subject?: string } = {},
    options: APIRequestOptions = {},
  ): Promise<Filters | null> {
    const queryParams = new URLSearchParams();
    if (params.type) {
      queryParams.append('type', params.type);
    }
    if (params.subject) {
      queryParams.append('subject', params.subject);
    }

    const queryString = queryParams.toString();
    const url = queryString ? `/v1/filters?${queryString}` : '/v1/filters';

    const response = await this.get(url, options);

    if (this.hasError(response)) {
      console.error(`Failed to get offerings filters`);
      return null;
    }

    const data = await this.toJson(response, FiltersSchema);
    return data;
  }

  /**
   * Get exact qualified offerings based on the provided payload.
   * @param body - The offering payload.
   * @param userIpAddress - Optional user IP address for geo-location fallbacks.
   * @param options - Optional request options.
   * @returns The exact offering response.
   */
  async getQualifiedExactOfferings(
    body: OfferingPayload,
    userIpAddress?: string | null,
    options: APIRequestOptions = {},
  ): Promise<ExactOfferingResponse> {
    const headers: Record<string, string> = {};
    if (userIpAddress) {
      headers['x-forwarded-for'] = userIpAddress;
    }

    const response = await this.post(`/v1/offerings/qualified/exact`, body, {
      ...options,
      headers: { ...options.headers, ...headers },
    });

    if (this.hasError(response)) {
      console.error(`Failed to get qualified exact offerings`);
    }

    const data = await this.toJson(response, ExactOfferingResponseSchema);
    return data;
  }

  /**
   * Get program fields configuration for a specific degree.
   * @param params - Parameters containing the degreeId.
   * @param userIpAddress - Optional user IP address for geo-location fallbacks.
   * @param options - Optional request options.
   * @returns The degree fields configuration or null if request fails.
   */
  async getProgramFieldsConfiguration(
    params: { degreeId: string },
    userIpAddress?: string | null,
    options: APIRequestOptions = {},
  ): Promise<DegreeFieldsConfig | null> {
    const headers: Record<string, string> = {};
    if (userIpAddress) {
      headers['x-forwarded-for'] = userIpAddress;
    }

    const response = await this.get(`/v1/degree-fields/${params.degreeId}`, {
      ...options,
      headers: { ...options.headers, ...headers },
    });

    if (this.hasError(response)) {
      console.error(
        `Failed to get program fields configuration for degree ${params.degreeId}`,
      );
      return null;
    }

    const data = await this.toJson(response, DegreeFieldsConfigSchema);
    return data;
  }

  /**
   * Get course recommendations based on subjects.
   * @param params - Parameters containing the subjects array.
   * @param options - Optional request options.
   * @returns The recommendations response.
   */
  async getRecommendations(
    params: { subjects: string[] },
    options: APIRequestOptions = {},
  ): Promise<RecommendationsResponse> {
    const body = {
      courses: [
        {
          sourceId: '95f61afe-f3d7-4782-8210-618e9080bada',
          subjects: params.subjects,
        },
      ],
    };

    const response = await this.post(`/v1/recommendations`, body, options);

    if (this.hasError(response)) {
      console.error(`Failed to get recommendations`);
    }

    const data = await this.toJson(response, RecommendationsResponseSchema);
    return data;
  }

  /**
   * Get the academic dimension taxonomy (slug↔label) used by cascading
   * Configurable Experiences selects and DCS slug→label resolution.
   *
   * Served by the Offerings Service (the same base as the other
   * `/v1/offerings/*` methods). The endpoint returns the full catalog (it
   * can't be filtered server-side); callers scope what they keep downstream.
   * @param options - Optional request options (incl. Next `next` cache hint).
   * @returns The dimensions response, or null if the request fails.
   */
  async getDimensions(
    options: APIRequestOptions = {},
  ): Promise<DimensionsResponse | null> {
    const response = await this.get('/v1/offerings/dimensions', options);

    if (this.hasError(response)) {
      console.error('Failed to get offerings dimensions');
      return null;
    }

    return this.toJson(response, DimensionsResponseSchema);
  }

  /**
   * Search offerings based on the provided payload.
   * @param params - The search payload.
   * @param options - Optional request options.
   * @returns The search response.
   */
  async getSearchOfferings(
    params: OfferingsSearchPayload,
    options: APIRequestOptions = {},
  ): Promise<OfferingsSearchResponse> {
    const response = await this.post(`/v1/search`, params, options);

    if (this.hasError(response)) {
      console.error(`Failed to search offerings`);
    }

    const data = await this.toJson(response, OfferingsSearchResponseSchema);
    return data;
  }
}
